/**
 * Importar pegando (catálogo de precios, 6/10/2026).
 *
 * Una fila por línea; las columnas separadas por «|» o por tabulador (lo que
 * sale al copiar de Excel o de una tabla de un mensaje). Primero se ve la
 * VISTA PREVIA de lo que se va a crear: cada línea con lo que se ha entendido,
 * y las que no se entienden señaladas en rojo con el motivo. Al guardar solo
 * se crean las que se entienden; las demás no se guardan.
 *
 * Lo lee el servidor (`POST /api/catalogo-precios/importar/previa` y
 * `/importar`, `services/catalogo_precios.interpretar`), con la misma regla
 * para la vista previa y para guardar: aquí no se interpreta nada. Los
 * importes del texto de ayuda son inventados (6/10/2026: ni un importe de la
 * clínica en el código); la vista previa no deja fila en Control rastreo.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, Loader2, Save } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { COLUMNAS_IMPORTAR, COLUMNAS_IMPORTAR_ANTES, textoImporte, textoIva } from '../../lib/catalogoPrecios';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

// Qué se enseña de cada fila entendida, columna a columna. En una partida,
// `sin_iva` es la casilla «Sin IVA» (28/10/2026); en un forfait o un
// servicio, un importe.
const CELDAS = {
  tratamiento: ['nombre', 'precio', 'sin_iva', 'anestesia', 'duracion', 'nota'],
  cirugia: ['nombre', 'precio', 'sin_iva', 'anestesia', 'duracion', 'nota'],
  forfait: ['nombre', 'sin_iva', 'con_iva', 'estancia_sin_iva', 'estancia_con_iva'],
  servicio: ['nombre', 'sin_iva', 'con_iva', 'nota'],
};
const IMPORTES = new Set(['precio', 'sin_iva', 'con_iva', 'estancia_sin_iva', 'estancia_con_iva']);
const PARTIDAS = new Set(['tratamiento', 'cirugia']);

function esImporte(tipo, campo) {
  return IMPORTES.has(campo) && !(PARTIDAS.has(tipo) && campo === 'sin_iva');
}

function celda(tipo, datos, campo) {
  if (PARTIDAS.has(tipo) && campo === 'sin_iva') return textoIva(datos?.sin_iva);
  if (esImporte(tipo, campo)) return textoImporte(datos?.[campo], datos?.[`${campo}_matiz`]);
  const v = datos?.[campo];
  return v === null || v === undefined || v === '' ? '—' : String(v);
}

export default function ImportarPreciosDialog({ abierto, onClose, tipo, padreId = null, titulo, onGuardado }) {
  const [texto, setTexto] = useState('');
  const [previa, setPrevia] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (abierto) { setTexto(''); setPrevia(null); }
  }, [abierto]);

  const columnas = COLUMNAS_IMPORTAR[tipo] || [];
  const columnasAntes = COLUMNAS_IMPORTAR_ANTES[tipo] || null;
  const cuerpo = { tipo, padre_id: padreId || null, texto };

  const verPrevia = async () => {
    setCargando(true);
    try {
      const r = await api.post('/api/catalogo-precios/importar/previa', cuerpo);
      setPrevia(r?.data || null);
    } catch (e) {
      setPrevia(null);
      toast.error(mensajeError(e, 'No se ha podido leer el texto'));
    } finally {
      setCargando(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.post('/api/catalogo-precios/importar', cuerpo);
      const d = r?.data || {};
      const n = Number(d.guardadas || 0);
      const malas = Number(d.errores || 0);
      toast.success(`${n} ${n === 1 ? 'fila creada' : 'filas creadas'}`
        + (malas ? `; ${malas} sin entender, no ${malas === 1 ? 'guardada' : 'guardadas'}` : ''));
      onGuardado && onGuardado(d);
      onClose && onClose();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido importar'));
    } finally {
      setGuardando(false);
    }
  };

  const validas = Number(previa?.validas || 0);
  // Las columnas con las que el servidor ha leído la tabla ENTERA
  // (28/10/2026): en las partidas puede ser el formato de antes, y la vista
  // previa lo dice y pinta esas columnas.
  const columnasPrevia = Array.isArray(previa?.columnas) && previa.columnas.length ? previa.columnas : columnas;
  const camposPrevia = Array.isArray(previa?.campos) && previa.campos.length ? previa.campos : (CELDAS[tipo] || []);
  return (
    <Dialog open={!!abierto} onOpenChange={(o) => { if (!o && onClose) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>Importar: {titulo}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm" data-testid="importar-precios">
          <p className="text-muted-foreground">
            Pega una fila por línea, con las columnas separadas por «|» o por tabulador (lo que sale al
            copiar de Excel): <span className="font-medium text-[#13231E]" data-testid="importar-columnas">
              {columnas.join(' | ')}</span>. Importes como «1.234€», «2.345,60€», «~120€ + IVA» o
            «99€/noche». Las líneas con los títulos de las columnas se saltan.
            {columnasAntes && (
              <span data-testid="importar-columnas-antes">
                {' '}En la columna IVA, «Con IVA» o «Sin IVA» (vacía: con IVA; un precio escrito «+ IVA» también
                es sin IVA). Una tabla con las columnas de antes ({columnasAntes.join(' | ')}) se sigue entendiendo.
              </span>
            )}
          </p>
          <Textarea
            rows={8}
            value={texto}
            onChange={(e) => { setTexto(e.target.value); setPrevia(null); }}
            placeholder={columnas.join(' | ')}
            className="font-mono text-xs"
            data-testid="importar-texto"
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={verPrevia} disabled={!texto.trim() || cargando}
              data-testid="importar-previa">
              {cargando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
              Ver vista previa
            </Button>
          </div>

          {previa && (
            <div className="space-y-2" data-testid="importar-resultado">
              <p className="text-sm" data-testid="importar-resumen">
                <span className="font-semibold text-[#1E4A3F]">{validas} {validas === 1 ? 'fila se creará' : 'filas se crearán'}</span>
                {previa.errores ? <span className="text-red-700"> · {previa.errores} {previa.errores === 1 ? 'no se entiende y no se guardará' : 'no se entienden y no se guardarán'}</span> : null}
                {previa.ignoradas ? <span className="text-muted-foreground"> · {previa.ignoradas} {previa.ignoradas === 1 ? 'ignorada' : 'ignoradas'}</span> : null}
              </p>
              {previa.formato === 'antes' && (
                <p className="text-xs rounded bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5"
                  data-testid="importar-formato-antes">
                  Tabla con las columnas de antes: {columnasPrevia.join(' | ')}. Si querías la columna IVA o
                  la de anestesia, añádelas (en la tercera, «Con IVA» o «Sin IVA») y vuelve a ver la vista previa.
                </p>
              )}
              <div className="overflow-x-auto border border-border rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                    <tr>
                      <th className="py-2 px-2">Línea</th>
                      {columnasPrevia.map((c) => <th key={c} className="py-2 px-2">{c}</th>)}
                      <th className="py-2 px-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previa.filas || []).map((f) => (
                      <tr key={f.linea}
                        className={`border-t border-border ${f.estado === 'error' ? 'bg-red-50 text-red-900' : ''} ${f.estado === 'ignorada' ? 'text-muted-foreground' : ''}`}
                        data-testid="importar-fila" data-estado={f.estado}>
                        <td className="py-1.5 px-2 tabular-nums">{f.linea}</td>
                        {f.estado === 'ok'
                          ? camposPrevia.map((campo) => (
                            <td key={campo} className={`py-1.5 px-2 ${esImporte(tipo, campo) ? 'tabular-nums whitespace-nowrap' : ''}`}>
                              {celda(tipo, f.datos, campo)}
                            </td>))
                          : <td colSpan={columnasPrevia.length} className="py-1.5 px-2 font-mono break-all">{f.texto}</td>}
                        <td className="py-1.5 px-2">
                          {f.estado === 'ok' ? 'Se creará' : f.estado === 'error' ? `No se guarda: ${f.motivo}` : f.motivo}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <Button type="button" onClick={guardar} disabled={!validas || guardando}
                  className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="importar-guardar">
                  {guardando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Guardar {validas} {validas === 1 ? 'fila' : 'filas'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
